import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import StatisticsCollection from "./collection/StatisticsCollection";
import StatisticsQuality from "./quality/StatisticsQuality";
import { useTranslation } from "react-i18next";
import StatisticsCO2 from "./Co2/StatisticsCo2";
import StatisticsValorization from "./valorization/StatisticsValorization";
import { useHistory, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import PageHeader from "../../components/PageHeader";
import HowItWorksButton from "../../components/howItWorks/HowItWorksButton";
import StatisticsInlineFilters from "./components/StatisticsInlineFilters";
import StatisticsRecommendationsGuide from "./components/StatisticsRecommendationsGuide";
import CollectionAnomalyAlert from "./components/CollectionAnomalyAlert";
import QualityPriorityAlert from "./components/QualityPriorityAlert";
import { useStatisticsFilters } from "./hooks/useStatisticsFilters";
import { useStatisticsPeriod } from "./hooks/useStatisticsPeriod";
import { useStatisticsData } from "./hooks/useStatisticsData";
import { useStatisticsLastUpdate } from "./hooks/useStatisticsLastUpdate";
import StatisticsMetricsRefreshBar from "./components/StatisticsMetricsRefreshBar";
import { deltaMilliseconds } from "../../components/formatted/FormattedDateAndTime";
import { getWasteStatistic } from "../../services/container";
import { buildQualityInsights } from "./utils/qualityInsights";
import { mergeFluxes } from "./utils/statisticsUtils";
import {
  buildKeywordFilterValue,
  syncKeywordFiltersFromValue,
} from "../activity/utils/activityPageFilters";
import useWeeklyReportExport from "./weeklyReport/useWeeklyReportExport";
import PdfExportProgressOverlay from "../../components/loader/PdfExportProgressOverlay";

const RECOMMENDATIONS_GUIDE_BASE = "/statistics/recommendations";
const STATISTICS_TAB_IDS = new Set([
  "rotations",
  "quality",
  "valorization",
  "carbon",
]);

/** Path segment after /statistics/recommendations/ — arbitrary, no whitelist. */
const getRecommendationsGuideSectionFromPath = (pathname) => {
  if (!pathname.startsWith(RECOMMENDATIONS_GUIDE_BASE)) return null;
  const rest = pathname
    .slice(RECOMMENDATIONS_GUIDE_BASE.length)
    .replace(/^\//, "");
  const section = rest.split("/")[0];
  return section || null;
};

const Statistics = ({ token }) => {
  const location = useLocation();
  const history = useHistory();
  const viewMode = Cookies.get("viewMode") || "client";
  const isRecommendationsGuide = location.pathname.startsWith(
    RECOMMENDATIONS_GUIDE_BASE,
  );
  const recommendationsGuideSection = getRecommendationsGuideSectionFromPath(
    location.pathname,
  );
  const recommendationsGuideBackTab = STATISTICS_TAB_IDS.has(
    recommendationsGuideSection,
  )
    ? recommendationsGuideSection
    : "rotations";

  useEffect(() => {
    if (
      isRecommendationsGuide &&
      location.pathname.replace(/\/$/, "") === RECOMMENDATIONS_GUIDE_BASE
    ) {
      history.replace(`${RECOMMENDATIONS_GUIDE_BASE}/rotations`);
    }
  }, [history, isRecommendationsGuide, location.pathname]);

  const {
    activateCollectionField = true,
    activateQualityField = false,
    activateCarbonField = false,
  } = location.state || {};

  const { t } = useTranslation();
  const [showCollection, setShowCollection] = useState(activateCollectionField);
  const [showQuality, setShowQuality] = useState(activateQualityField);
  const [showCarbon, setShowCarbon] = useState(activateCarbonField);
  const [showValorization, setShowValorization] = useState(false);
  const [isAkanthasAccount, setIsAkanthasAccount] = useState(true);
  const [collectionAnomaly, setCollectionAnomaly] = useState(null);
  const [qualityPriority, setQualityPriority] = useState(null);
  const [fluxFilter, setFluxFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("all");
  const hasInitializedKeywordFilters = useRef(false);

  const { keywordFilter, setKeywordFilter, groupedOptions } =
    useStatisticsFilters();

  const {
    periodHistoryStart,
    periodHistoryStop,
    periodHistoryStartPrevious,
    setPeriodHistoryStartPrevious,
    periodHistoryStopPrevious,
    setPeriodHistoryStopPrevious,
    periodFilter,
    periodDisplayLabel,
    applyPeriod,
    handleValidate,
    setHandleValidate,
  } = useStatisticsPeriod();

  const { numberOfContainers, numberOfSite, activatedSortingErrorOption } =
    useStatisticsData(keywordFilter, periodHistoryStart, periodHistoryStop);
  const enablePreviousPeriod = periodFilter !== null;
  const canAccessQuality =
    activatedSortingErrorOption || (isAkanthasAccount && viewMode === "admin");

  const { lastUpdate } = useStatisticsLastUpdate({
    periodHistoryStart,
    periodHistoryStop,
    keywordFilter,
    handleValidate,
  });

  const withAllOption = useCallback(
    (options) => [{ label: t("All"), value: "all" }, ...options],
    [t],
  );

  const fluxOptions = useMemo(
    () => withAllOption(groupedOptions.flux),
    [groupedOptions.flux, withAllOption],
  );
  const siteOptions = useMemo(
    () => withAllOption(groupedOptions.address),
    [groupedOptions.address, withAllOption],
  );
  const zoneOptions = useMemo(
    () => withAllOption(groupedOptions.zone),
    [groupedOptions.zone, withAllOption],
  );

  useEffect(() => {
    if (hasInitializedKeywordFilters.current || !keywordFilter?.value?.length) {
      return;
    }

    const synced = syncKeywordFiltersFromValue(keywordFilter);
    setZoneFilter(synced.zoneFilter);
    setFluxFilter(synced.fluxFilter);
    setSiteFilter(synced.addressFilter);
    hasInitializedKeywordFilters.current = true;
  }, [keywordFilter]);

  const applyKeywordFilters = useCallback(
    (patch) => {
      const next = {
        zoneFilter,
        fluxFilter,
        addressFilter: siteFilter,
        ...patch,
      };

      if (patch.zoneFilter !== undefined) setZoneFilter(patch.zoneFilter);
      if (patch.fluxFilter !== undefined) setFluxFilter(patch.fluxFilter);
      if (patch.addressFilter !== undefined) {
        setSiteFilter(patch.addressFilter);
      }

      setKeywordFilter(
        buildKeywordFilterValue({
          ...next,
          optionsByValue: groupedOptions.byValue,
        }),
      );
      setHandleValidate((previous) => previous + 1);
    },
    [
      zoneFilter,
      fluxFilter,
      siteFilter,
      groupedOptions.byValue,
      setKeywordFilter,
      setHandleValidate,
    ],
  );

  const handleClearFilters = useCallback(() => {
    setZoneFilter("all");
    setFluxFilter("all");
    setSiteFilter("all");
    setKeywordFilter({ value: [] });
    setHandleValidate((previous) => previous + 1);
  }, [setKeywordFilter, setHandleValidate]);

  const weeklyReportSiteOptions = useMemo(
    () =>
      Object.values(groupedOptions.byValue || {}).filter(
        (option) => option.type === "address",
      ),
    [groupedOptions.byValue],
  );
  const selectedSiteKey =
    (siteFilter !== "all" ? groupedOptions.byValue?.[siteFilter]?.key : null) ??
    (keywordFilter?.value ?? []).find((option) => option.type === "address")
      ?.key ??
    null;
  const {
    exportWeeklyReport,
    canExport: canExportWeeklyReport,
    isExporting: isExportingWeeklyReport,
    progress: weeklyReportProgress,
    progressLabel: weeklyReportProgressLabel,
  } = useWeeklyReportExport({
    periodHistoryStart,
    periodHistoryStop,
    keywordFilter,
    selectedSiteKey,
    siteOptions: weeklyReportSiteOptions,
    numberOfSite,
  });

  useEffect(() => {
    const email = Cookies.get("userEmail") || "";
    setIsAkanthasAccount(email.includes("@akanthas.com"));
  }, []);

  useEffect(() => {
    setHandleValidate(true);
  }, [keywordFilter, setHandleValidate]);

  useEffect(() => {
    if (periodHistoryStart && periodHistoryStop) {
      if (!enablePreviousPeriod) {
        setPeriodHistoryStartPrevious(null);
        setPeriodHistoryStopPrevious(null);
        setHandleValidate((prev) => prev + 1);
        return;
      }

      const start = new Date(periodHistoryStart);
      const stop = new Date(periodHistoryStop);
      const delta = deltaMilliseconds(start, stop);
      const startPrevious = new Date(start.getTime() - delta);

      setPeriodHistoryStartPrevious(startPrevious.toISOString());
      setPeriodHistoryStopPrevious(start.toISOString());
      setHandleValidate((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodHistoryStart, periodHistoryStop, enablePreviousPeriod]);

  useEffect(() => {
    if (!canAccessQuality || !periodHistoryStart || !periodHistoryStop) {
      setQualityPriority(null);
      return;
    }

    let isCancelled = false;
    const keywordValue =
      keywordFilter &&
      typeof keywordFilter === "object" &&
      "value" in keywordFilter
        ? keywordFilter.value
        : keywordFilter || [];

    getWasteStatistic({
      periodStart: periodHistoryStart,
      periodStop: periodHistoryStop,
      keywordFilter: JSON.stringify(keywordValue),
    })
      .then((response) => {
        if (isCancelled || response.status !== 200) return;

        const statisticsData = response.data?.statisticsData ?? {};
        const materials =
          JSON.parse(localStorage.getItem("materialsDescription")) || [];
        const mergeConfig = materials.map((material) => ({
          referenceName: material.referenceName,
          possibleNames: material.possibleNames,
        }));

        const insights = buildQualityInsights({
          ...statisticsData,
          dataSortingError: mergeFluxes(
            statisticsData.dataSortingError,
            mergeConfig,
          ),
          countAnomalies:
            statisticsData.countAnomalies ??
            Object.values(statisticsData.dataResult ?? {}).reduce(
              (sum, flux) => sum + (flux?.countAnomalies ?? 0),
              0,
            ),
        });

        setQualityPriority(insights.priorityAction);
      })
      .catch(() => {
        if (!isCancelled) {
          setQualityPriority(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    canAccessQuality,
    periodHistoryStart,
    periodHistoryStop,
    keywordFilter,
    handleValidate,
  ]);

  const helpTopic = showCollection
    ? "performance"
    : showQuality
      ? "performanceQuality"
      : showValorization
        ? "performanceValorization"
        : "performanceCarbon";

  const exitRecommendationsGuide = useCallback(
    (tabId = recommendationsGuideBackTab) => {
      const resolvedTab = STATISTICS_TAB_IDS.has(tabId)
        ? tabId
        : recommendationsGuideBackTab;
      setShowCollection(resolvedTab === "rotations");
      setShowQuality(resolvedTab === "quality");
      setShowValorization(resolvedTab === "valorization");
      setShowCarbon(resolvedTab === "carbon");
      history.push("/statistics");
    },
    [history, recommendationsGuideBackTab],
  );

  if (token) {
    return (
      <div className="settings-page-wrapper">
        <PageHeader
          title={t("Performance")}
          subtitle={
            numberOfContainers > 0
              ? `${t("statistics.siteCount", { count: numberOfSite })}, ${t(
                  "statistics.zoneCount",
                  {
                    count: numberOfContainers,
                  },
                )}`
              : null
          }
          showBackButton={isRecommendationsGuide}
          backLabel={`${t("Back to")} ${
            recommendationsGuideBackTab === "valorization"
              ? t("Valorization")
              : recommendationsGuideBackTab === "quality"
                ? t("sorting_errors")
                : recommendationsGuideBackTab === "carbon"
                  ? t("Carbon Footprint")
                  : t("Rotations")
          }`}
          onBackClick={() =>
            exitRecommendationsGuide(recommendationsGuideBackTab)
          }
          tabs={[
            { id: "rotations", label: t("Rotations") },
            ...(activatedSortingErrorOption ||
            (isAkanthasAccount && viewMode === "admin")
              ? [{ id: "quality", label: t("sorting_errors") }]
              : []),
            { id: "valorization", label: t("Valorization") },
            { id: "carbon", label: t("Carbon Footprint") },
          ]}
          activeTab={
            isRecommendationsGuide
              ? recommendationsGuideBackTab
              : showCollection
                ? "rotations"
                : showQuality
                  ? "quality"
                  : showValorization
                    ? "valorization"
                    : showCarbon
                      ? "carbon"
                      : ""
          }
          onTabChange={(tabId) => {
            if (isRecommendationsGuide) {
              exitRecommendationsGuide(tabId);
              return;
            }
            setShowCollection(tabId === "rotations");
            setShowQuality(tabId === "quality");
            setShowValorization(tabId === "valorization");
            setShowCarbon(tabId === "carbon");
          }}
          actions={
            !isRecommendationsGuide &&
            isAkanthasAccount &&
            viewMode === "admin" ? (
              <StatisticsMetricsRefreshBar
                lastUpdate={lastUpdate}
                periodHistoryStart={periodHistoryStart}
                periodHistoryStop={periodHistoryStop}
                keywordFilter={keywordFilter}
                onRefreshQueued={() => {
                  setTimeout(() => setHandleValidate((prev) => prev + 1), 1500);
                }}
                onExportWeeklyReport={exportWeeklyReport}
                canExportWeeklyReport={canExportWeeklyReport}
                isExportingWeeklyReport={isExportingWeeklyReport}
              />
            ) : null
          }
        />
        <div className="settings-content-wrapper">
          <div className="statistics-page">
            {!isRecommendationsGuide && (
              <>
                <div className="statistics-toolbar">
                  <div className="statistics-toolbar__filters">
                    <StatisticsInlineFilters
                      periodDisplayLabel={periodDisplayLabel}
                      periodFilter={periodFilter}
                      periodHistoryStart={periodHistoryStart}
                      periodHistoryStop={periodHistoryStop}
                      onApplyPeriod={applyPeriod}
                      fluxOptions={fluxOptions}
                      fluxFilter={fluxFilter}
                      onApplyFlux={(value) =>
                        applyKeywordFilters({ fluxFilter: value })
                      }
                      siteOptions={siteOptions}
                      siteFilter={siteFilter}
                      onApplySite={(value) =>
                        applyKeywordFilters({ addressFilter: value })
                      }
                      zoneOptions={zoneOptions}
                      zoneFilter={zoneFilter}
                      onApplyZone={(value) =>
                        applyKeywordFilters({ zoneFilter: value })
                      }
                      showZoneFilter
                      onClearFilters={handleClearFilters}
                    />
                  </div>
                  <div className="statistics-toolbar__help">
                    <HowItWorksButton
                      topic={helpTopic}
                      context={
                        helpTopic === "performance" ||
                        helpTopic === "performanceValorization"
                          ? { showRecommendationTypes: true }
                          : undefined
                      }
                    />
                  </div>
                </div>
                {showCollection && (
                  <CollectionAnomalyAlert anomaly={collectionAnomaly} />
                )}
                {showQuality && (
                  <QualityPriorityAlert priorityAction={qualityPriority} />
                )}
              </>
            )}

            {isRecommendationsGuide ? (
              <StatisticsRecommendationsGuide
                section={recommendationsGuideSection}
              />
            ) : null}

            {!isRecommendationsGuide && showCollection ? (
              <StatisticsCollection
                periodHistoryStart={periodHistoryStart}
                periodHistoryStop={periodHistoryStop}
                periodHistoryStartPrevious={periodHistoryStartPrevious}
                periodHistoryStopPrevious={periodHistoryStopPrevious}
                enablePreviousPeriod={enablePreviousPeriod}
                keywordFilter={keywordFilter}
                numberOfContainers={numberOfContainers}
                numberOfSite={numberOfSite}
                handleValidate={handleValidate}
                setHandleValidate={setHandleValidate}
                onAnomalyChange={setCollectionAnomaly}
              />
            ) : null}

            {!isRecommendationsGuide && showQuality ? (
              <StatisticsQuality
                periodHistoryStart={periodHistoryStart}
                periodHistoryStop={periodHistoryStop}
                periodHistoryStartPrevious={periodHistoryStartPrevious}
                periodHistoryStopPrevious={periodHistoryStopPrevious}
                enablePreviousPeriod={enablePreviousPeriod}
                keywordFilter={keywordFilter}
                handleValidate={handleValidate}
                setHandleValidate={setHandleValidate}
              />
            ) : null}
            {!isRecommendationsGuide && showCarbon ? (
              <StatisticsCO2
                periodHistoryStart={periodHistoryStart}
                periodHistoryStop={periodHistoryStop}
                periodHistoryStartPrevious={periodHistoryStartPrevious}
                periodHistoryStopPrevious={periodHistoryStopPrevious}
                enablePreviousPeriod={enablePreviousPeriod}
                keywordFilter={keywordFilter}
                handleValidate={handleValidate}
                setHandleValidate={setHandleValidate}
              />
            ) : null}
            {!isRecommendationsGuide && showValorization ? (
              <StatisticsValorization
                periodHistoryStart={periodHistoryStart}
                periodHistoryStop={periodHistoryStop}
                periodHistoryStartPrevious={periodHistoryStartPrevious}
                periodHistoryStopPrevious={periodHistoryStopPrevious}
                enablePreviousPeriod={enablePreviousPeriod}
                keywordFilter={keywordFilter}
                handleValidate={handleValidate}
                setHandleValidate={setHandleValidate}
                onOpenRecommendationsGuide={() => {
                  history.push(`${RECOMMENDATIONS_GUIDE_BASE}/valorization`);
                }}
              />
            ) : null}
          </div>
        </div>
        {isExportingWeeklyReport ? (
          <PdfExportProgressOverlay
            percent={weeklyReportProgress}
            label={weeklyReportProgressLabel}
          />
        ) : null}
      </div>
    );
  }
};

export default Statistics;
